import React, {
    useEffect,
    useImperativeHandle,
    useState,
    useRef,
    forwardRef,
} from "react";
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Divider,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    InputAdornment,
    Typography,
} from "@mui/material";
import { Save, Delete, EmojiEmotions } from "@mui/icons-material";
import EmojiPicker from "emoji-picker-react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import {
    waCreateOpcion,
    waUpdateOpcion,
    waDeleteOpcion,
    waGetOpcionesByMenu,
    waUploadEnvio,
} from "../wa/api/wa_menu_Api";

const TIPOS = [
    { value: "texto", valueLabel: "Texto" },
    { value: "submenu", valueLabel: "Submenú" },
    { value: "contacto", valueLabel: "Contacto" },
    { value: "enviar_archivo", valueLabel: "Enviar archivo" },
];

function insertEmojiAtInput(input, emoji) {
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const before = input.value.slice(0, start);
    const after = input.value.slice(end);
    const next = before + emoji + after;
    const caret = start + emoji.length;
    return { value: next, caret };
}

const WaOpcionEditor = forwardRef(({ menuId, onSaved, onPendingChange }, ref) => {
    const [opciones, setOpciones] = useState([]);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const [adding, setAdding] = useState(false);
    const [picker, setPicker] = useState({ open: false, field: null, index: null });
    const lastRef = useRef(null);
    const refs = useRef({});

    useImperativeHandle(ref, () => ({
        addNewOption: handleAddOpcion,
    }));

    useEffect(() => {
        if (!menuId) return;
        loadOpciones();
    }, [menuId]);

    const loadOpciones = async () => {
        const res = await waGetOpcionesByMenu(menuId);
        setOpciones(res || []);
    };

    const scrollToLast = () => {
        setTimeout(() => {
            if (lastRef.current) {
                lastRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 200);
    };

    const handleAddOpcion = () => {
        if (adding) return;
        setAdding(true);
        onPendingChange?.(true);
        setOpciones((prev) => {
            const nueva = {
                id: null,
                menu_id: menuId,
                texto_opcion: "",
                valor_accion: "",
                tipo_accion: "texto",
                mensaje_confirmacion: "",
                instruccion: "",
                requiere_texto: false,
                requiere_archivo: false,
                enviar_sms: false,
                visible_para_sector: "",
                siguiente_menu_id: "",
                orden: (prev?.length || 0) + 1,
                activo: true,
                archivo_a_enviar: "",
                archivo_caption: "",
            };
            const actualizadas = [...prev, nueva];
            setTimeout(scrollToLast, 400);
            return actualizadas;
        });
    };

    const handleChange = (index, field, value) => {
        setOpciones((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleSectorChange = (index, sector, checked) => {
        setOpciones((prev) => {
            const updated = [...prev];
            const sectores =
                updated[index].visible_para_sector
                    ?.split(",")
                    .map((x) => x.trim())
                    .filter(Boolean) || [];
            if (checked && !sectores.includes(sector)) sectores.push(sector);
            if (!checked)
                updated[index].visible_para_sector = sectores
                    .filter((x) => x !== sector)
                    .join(",");
            else updated[index].visible_para_sector = sectores.join(",");
            return updated;
        });
    };

    /*     const handleSave = async (op) => {
            try {
                const toSave = { ...op };
                toSave.orden = Number(toSave.orden) || 0;
                if (op.id) await waUpdateOpcion(op.id, toSave);
                else await waCreateOpcion(toSave);
    
                setSnack({ open: true, msg: "✅ Opción guardada", sev: "success" });
                setAdding(false);
                onPendingChange?.(false);
                await loadOpciones();
                onSaved?.();
            } catch {
                setSnack({ open: true, msg: "❌ Error al guardar", sev: "error" });
            }
        }; */

    const handleSave = async (op) => {
        try {
            const toSave = { ...op };
            toSave.orden = Number(toSave.orden) || 0;

            // 🧩 Normalizar campo siguiente_menu_id (para evitar error de MySQL)
            if (toSave.siguiente_menu_id === "" || toSave.siguiente_menu_id === undefined) {
                toSave.siguiente_menu_id = null;
            } else {
                toSave.siguiente_menu_id = Number(toSave.siguiente_menu_id);
            }

            if (op.id) await waUpdateOpcion(op.id, toSave);
            else await waCreateOpcion(toSave);

            setSnack({ open: true, msg: "✅ Opción guardada", sev: "success" });
            setAdding(false);
            onPendingChange?.(false);
            await loadOpciones();
            onSaved?.();
        } catch {
            setSnack({ open: true, msg: "❌ Error al guardar", sev: "error" });
        }
    };


    const handleDelete = async (id) => {
        if (!id) return;
        if (!window.confirm("¿Eliminar esta opción?")) return;
        try {
            await waDeleteOpcion(id);
            setSnack({ open: true, msg: "🗑️ Opción eliminada", sev: "info" });
            setAdding(false);
            onPendingChange?.(false);
            await loadOpciones();
            onSaved?.();
        } catch {
            setSnack({ open: true, msg: "❌ Error al eliminar", sev: "error" });
        }
    };

    const openPicker = (field, index) => setPicker({ open: true, field, index });
    const closePicker = () => setPicker({ open: false, field: null, index: null });

    const onEmojiClick = (emojiData) => {
        const { field, index } = picker;
        const input = refs.current?.[`${field}-${index}`];
        if (!input) return closePicker();
        const inserted = insertEmojiAtInput(input, emojiData.emoji);
        if (!inserted) return closePicker();
        handleChange(index, field, inserted.value);
        setTimeout(() => {
            input.focus();
            if (typeof inserted.caret === "number")
                input.setSelectionRange(inserted.caret, inserted.caret);
        }, 0);
        closePicker();
    };

    const handleUploadEnvio = async (e, index) => {
        try {
            const file = e.target.files?.[0];
            if (!file) return;
            const data = await waUploadEnvio(file);
            if (!data.ok) {
                setSnack({ open: true, msg: "❌ Error al subir archivo", sev: "error" });
                return;
            }
            setSnack({ open: true, msg: "✅ Archivo subido", sev: "success" });
            setOpciones((prev) => {
                const updated = [...prev];
                updated[index].archivo_a_enviar = data.archivo;
                return updated;
            });
        } catch {
            setSnack({ open: true, msg: "❌ Error al subir archivo", sev: "error" });
        } finally {
            e.target.value = "";
        }
    };

    const verArchivo = async (nombreArchivo) => {
        if (!nombreArchivo) return;
        try {
            const API = (localStorage.getItem("API") || "https://wabot.netizar.com").replace(/\/+$/, "");
            const token = localStorage.getItem("token") || "";
            const { data, headers } = await axios.get(`${API}/files/envios/${nombreArchivo}`, {
                responseType: "blob",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const blob = new Blob([data], { type: headers["content-type"] || "application/octet-stream" });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, "_blank");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        } catch {
            setSnack({ open: true, msg: "❌ No se pudo abrir el archivo", sev: "error" });
        }
    };

    const withEmoji = (field, index) => ({
        endAdornment: (
            <InputAdornment position="end">
                <Tooltip title="Insertar emoji">
                    <IconButton size="small" onClick={() => openPicker(field, index)}>
                        <EmojiEmotions fontSize="small" />
                    </IconButton>
                </Tooltip>
            </InputAdornment>
        ),
    });

    return (
        <Box sx={{ p: 2 }}>
            {opciones.map((op, i) => (
                <Box
                    key={op.id ?? `n-${i}`}
                    ref={i === opciones.length - 1 ? lastRef : null}
                    sx={{
                        mb: 3,
                        p: 3,
                        border: "1px solid #e0e0e0",
                        borderRadius: 3,
                        backgroundColor: i % 2 === 0 ? "#f5dfaa" : "#f2dfff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                            <Grid container spacing={2}>
                                {/* Orden, Texto, Clave */}
                                <Grid item xs={12} sm={2.5}>
                                    <TextField
                                        label="Orden"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={op.orden ?? 0}
                                        onChange={(e) => handleChange(i, "orden", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Texto opción"
                                        fullWidth
                                        size="small"
                                        value={op.texto_opcion || ""}
                                        onChange={(e) => handleChange(i, "texto_opcion", e.target.value)}
                                        inputRef={(el) => (refs.current[`texto_opcion-${i}`] = el)}
                                        InputProps={withEmoji("texto_opcion", i)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3.5}>
                                    <TextField
                                        label="Clave interna"
                                        fullWidth
                                        size="small"
                                        value={op.valor_accion || ""}
                                        onChange={(e) => handleChange(i, "valor_accion", e.target.value)}
                                    />
                                </Grid>

                                {/* Tipo + mensaje */}
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        select
                                        label="Tipo"
                                        fullWidth
                                        size="small"
                                        value={op.tipo_accion}
                                        onChange={(e) => handleChange(i, "tipo_accion", e.target.value)}
                                    >
                                        {TIPOS.map((t) => (
                                            <MenuItem key={t.value} value={t.value}>
                                                {t.valueLabel}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                {/* Campo Submenú destino, visible solo si el tipo es "submenu" */}
                                {op.tipo_accion === "submenu" && (
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="Submenú destino (ID)"
                                            type="number"
                                            fullWidth
                                            size="small"
                                            value={op.siguiente_menu_id || ""}
                                            onChange={(e) => handleChange(i, "siguiente_menu_id", e.target.value)}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={9}>
                                    <TextField
                                        label="Mensaje de confirmación"
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={2}
                                        value={op.mensaje_confirmacion || ""}
                                        onChange={(e) => handleChange(i, "mensaje_confirmacion", e.target.value)}
                                        inputRef={(el) => (refs.current[`mensaje_confirmacion-${i}`] = el)}
                                        InputProps={withEmoji("mensaje_confirmacion", i)}
                                    />
                                </Grid>

                                {/* Instrucción / Archivo */}
                                <Grid item xs={12}>
                                    {op.tipo_accion === "enviar_archivo" ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                                p: 2,
                                                border: "1px dashed #ccc",
                                                borderRadius: 2,
                                                backgroundColor: "#fefefe",
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                                                Archivo a enviar
                                            </Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    size="small"
                                                    startIcon={<UploadFileIcon />}
                                                >
                                                    Subir archivo
                                                    <input
                                                        type="file"
                                                        hidden
                                                        onChange={(e) => handleUploadEnvio(e, i)}
                                                    />
                                                </Button>
                                                {op.archivo_a_enviar && (
                                                    <>
                                                        <Tooltip title={op.archivo_a_enviar}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    maxWidth: 220,
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}
                                                            >
                                                                {op.archivo_a_enviar}
                                                            </Typography>
                                                        </Tooltip>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => verArchivo(op.archivo_a_enviar)}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                            <TextField
                                                label="Caption que acompañará al archivo"
                                                size="small"
                                                fullWidth
                                                value={op.archivo_caption || ""}
                                                onChange={(e) =>
                                                    handleChange(i, "archivo_caption", e.target.value)
                                                }
                                                inputRef={(el) =>
                                                    (refs.current[`archivo_caption-${i}`] = el)
                                                }
                                                InputProps={withEmoji("archivo_caption", i)}
                                            />
                                        </Box>
                                    ) : (
                                        <TextField
                                            label="Instrucción personalizada"
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            value={op.instruccion || ""}
                                            onChange={(e) => handleChange(i, "instruccion", e.target.value)}
                                            inputRef={(el) => (refs.current[`instruccion-${i}`] = el)}
                                            InputProps={withEmoji("instruccion", i)}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Panel derecho */}
                        <Grid item xs={12} md={3}>
                            <Box
                                sx={{
                                    borderLeft: { md: "1px solid #eee" },
                                    pl: { md: 2 },
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={op.visible_para_sector?.includes("16") || false}
                                                onChange={(e) => handleSectorChange(i, "16", e.target.checked)}
                                            />
                                        }
                                        label="Ver x Med. Laboral"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={op.enviar_sms || false}
                                                onChange={(e) => handleChange(i, "enviar_sms", e.target.checked)}
                                            />
                                        }
                                        label="📱 Enviar SMS"
                                    />
                                    <Divider sx={{ my: 1 }} />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={op.requiere_texto || false}
                                                onChange={(e) => handleChange(i, "requiere_texto", e.target.checked)}
                                            />
                                        }
                                        label="Requiere texto"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={op.requiere_archivo || false}
                                                onChange={(e) =>
                                                    handleChange(i, "requiere_archivo", e.target.checked)
                                                }
                                            />
                                        }
                                        label="Requiere archivo"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={op.activo || false}
                                                onChange={(e) => handleChange(i, "activo", e.target.checked)}
                                            />
                                        }
                                        label="Activo"
                                    />
                                </Box>

                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                                    <Tooltip title="Guardar">
                                        <IconButton color="primary" onClick={() => handleSave(op)}>
                                            <Save />
                                        </IconButton>
                                    </Tooltip>
                                    {op.id && (
                                        <Tooltip title="Eliminar">
                                            <IconButton color="error" onClick={() => handleDelete(op.id)}>
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            ))}

            {/* Picker de emojis */}
            <Dialog open={picker.open} onClose={closePicker} maxWidth="xs" fullWidth>
                <DialogTitle>Seleccioná un emoji</DialogTitle>
                <DialogContent>
                    <EmojiPicker
                        onEmojiClick={onEmojiClick}
                        searchDisabled={false}
                        previewConfig={{ showPreview: false }}
                        height={360}
                        width="100%"
                    />
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack({ ...snack, open: false })}
                    severity={snack.sev}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
});

export default WaOpcionEditor;
